import React, { useEffect, useState } from 'react';
import { Button, Message } from 'semantic-ui-react';
import {
  parseModelPreviewResponseV822,
  modelRuleErrorTextV1015,
} from './channelModelRulesV822';
import { channelModelsTextV107 } from './channelCopyV107';

const ChannelModelPreviewV822 = ({
  draft,
  channel,
  scopeKey,
  disabled = false,
  requestPreview,
  copyText,
  t,
}) => {
  const fingerprint = JSON.stringify([scopeKey, disabled, draft]);
  const copyFingerprint = JSON.stringify([fingerprint, channel]);
  const bodyText = JSON.stringify(draft.body);
  const [result, setResult] = useState(null);
  const [copyStatus, setCopyStatus] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setResult(null);
    setCopyStatus(null);
    if (disabled || draft.error || bodyText === 'null') return undefined;
    const timer = setTimeout(async () => {
      try {
        const response = await requestPreview(JSON.parse(bodyText), {
          signal: controller.signal,
        });
        const parsed = parseModelPreviewResponseV822(response);
        if (active) setResult({ fingerprint, status: 'ready', ...parsed });
      } catch (error) {
        if (active) setResult({
          fingerprint,
          status: 'error',
          message:
            error.response?.status === 400 &&
            /^(final public model ID |invalid final public model ID )/.test(
              error.response?.data?.message || ''
            )
              ? error.response.data.message
              : '',
        });
      }
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
      controller.abort();
    };
  }, [fingerprint, bodyText, disabled, draft.error, requestPreview, attempt]);

  const current = result?.fingerprint === fingerprint ? result : null;
  const copyValue = async (value) => {
    try {
      const copied = await copyText(value);
      setCopyStatus({
        fingerprint: copyFingerprint,
        status: copied === false ? 'copy_failed' : 'copied',
      });
    } catch {
      setCopyStatus({ fingerprint: copyFingerprint, status: 'copy_failed' });
    }
  };

  return (
    <section
      className='channel-model-preview-v822'
      aria-label={t('channel.edit.model_rules_v822.preview_title')}
    >
      <h4>{t('channel.edit.model_rules_v822.preview_title')}</h4>
      <p>{t('channel.edit.model_rules_v822.preview_notice')}</p>
      {disabled ? (
        <Message role='status'>
          {t('channel.edit.model_rules_v822.preview_waiting')}
        </Message>
      ) : draft.error ? (
        <Message visible warning role='alert'>
          {modelRuleErrorTextV1015(draft, t)}
        </Message>
      ) : current?.status === 'error' ? (
        <Message visible negative role='alert'>
          {current.message || t('channel.edit.model_rules_v822.errors.preview_failed')}
          <Button
            type='button'
            size='small'
            onClick={() => setAttempt((value) => value + 1)}
          >
            {t('channel.edit.model_rules_v822.retry')}
          </Button>
        </Message>
      ) : current?.status === 'ready' ? (
        <>
          <Button
            type='button'
            size='small'
            disabled={!current.modelIds.length}
            onClick={() =>
              copyValue(channelModelsTextV107(channel, current.modelIds, t))
            }
          >
            {t('channel.edit.model_rules_v822.copy_all')}
          </Button>
          {!current.modelIds.length && (
            <Message>
              {t('channel.edit.model_rules_v822.preview_empty')}
            </Message>
          )}
          <div className='channel-model-preview-v822__ids'>
            {current.modelIds.map((modelId) => (
              <Button
                type='button'
                basic
                key={modelId}
                title={t('channel.edit.model_rules_v822.copy_id')}
                onClick={() => copyValue(modelId)}
              >
                {modelId}
              </Button>
            ))}
          </div>
        </>
      ) : (
        <Message role='status'>
          {t('channel.edit.model_rules_v822.preview_loading')}
        </Message>
      )}
      {copyStatus?.fingerprint === copyFingerprint && (
        <span role='status'>
          {t(`channel.edit.model_rules_v822.${copyStatus.status}`)}
        </span>
      )}
    </section>
  );
};

export default ChannelModelPreviewV822;
